import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { mdiCheckAll, mdiCallMerge } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import { notify } from '../../lib/notifications'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'

interface ImportProposalsReviewProps {
    org: string
    importUid: string
}

/** The approve endpoint takes at most this many uids per call. */
const APPROVE_BATCH = 200

/**
 * The batch review of a deposit (spec 2026-10-04-02 §8): every site, building
 * and parcel the pipeline proposed from this import's documents, in one list.
 * Two proposals of one place merge into one; approve-all validates the whole
 * deposit, which re-queues the contract and stakeholder extraction the approval
 * gate held back.
 */
export function ImportProposalsReview({ org, importUid }: ImportProposalsReviewProps) {
    const { t } = useTranslation('imports')
    const queryClient = useQueryClient()
    const [selected, setSelected] = useState<string[]>([])
    const [busy, setBusy] = useState(false)

    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/imports/{importUid}/proposals', {
        params: { path: { orgCode: org, importUid } },
    })
    const proposals = data?.data ?? []
    if (proposals.length === 0) return null

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/imports/{importUid}/proposals'] })
        queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/imports/{importUid}/summary'] })
        setSelected([])
    }
    const failed = () =>
        notify({
            key: 'import-proposals-error',
            variant: 'destructive',
            titleKey: 'imports:proposals.error_title',
            descriptionKey: 'imports:proposals.error_body',
        })

    const toggle = (uid: string) =>
        setSelected((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]))

    const approveAll = async () => {
        setBusy(true)
        try {
            const uids = proposals.map((p) => p.uid)
            for (let i = 0; i < uids.length; i += APPROVE_BATCH) {
                const { error } = await apiClient.POST('/api/v1/organizations/{orgCode}/assets/approve', {
                    params: { path: { orgCode: org } },
                    body: { uids: uids.slice(i, i + APPROVE_BATCH), action: 'approve' },
                })
                if (error) throw new Error('approve failed')
            }
            refresh()
        } catch {
            failed()
        } finally {
            setBusy(false)
        }
    }

    // The first selected proposal is kept; the second is merged into it.
    const [keepUid, mergeUid] = selected
    const keep = proposals.find((p) => p.uid === keepUid)
    const merged = proposals.find((p) => p.uid === mergeUid)
    const canMerge = selected.length === 2 && keep && merged && keep.asset_type === merged.asset_type

    const merge = async () => {
        if (!canMerge) return
        setBusy(true)
        try {
            const { error } = await apiClient.POST('/api/v1/organizations/{orgCode}/assets/merge', {
                params: { path: { orgCode: org } },
                body: { source_uid: mergeUid, target_uid: keepUid },
            })
            if (error) throw new Error('merge failed')
            refresh()
        } catch {
            failed()
        } finally {
            setBusy(false)
        }
    }

    return (
        <section data-testid="import-proposals" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground">{t('proposals.title')}</h2>
                    <p className="text-xs text-muted-foreground">{t('proposals.description')}</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={!canMerge || busy}
                        onClick={merge}
                        data-testid="import-proposals-merge"
                    >
                        <AppIcon path={mdiCallMerge} size="sm" />
                        {canMerge ? t('proposals.merge_into', { name: keep?.name }) : t('proposals.merge')}
                    </Button>
                    <Button size="sm" disabled={busy} onClick={approveAll} data-testid="import-proposals-approve-all">
                        <AppIcon path={mdiCheckAll} size="sm" />
                        {t('proposals.approve_all', { count: proposals.length })}
                    </Button>
                </div>
            </div>
            <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                {proposals.map((p) => (
                    <li key={p.uid} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid="import-proposal-row">
                        <Checkbox
                            checked={selected.includes(p.uid)}
                            onCheckedChange={() => toggle(p.uid)}
                            aria-label={p.name}
                            data-testid={`import-proposal-select-${p.uid}`}
                        />
                        <Link
                            to="/$org/assets/$assetUid"
                            params={{ org, assetUid: p.uid }}
                            className="min-w-0 flex-1 truncate font-medium hover:underline"
                        >
                            {p.name}
                        </Link>
                        {p.parent_name && <span className="truncate text-xs text-muted-foreground">{p.parent_name}</span>}
                        <Badge variant="outline">{t(`proposals.type.${p.asset_type}`, { defaultValue: p.asset_type })}</Badge>
                        <span className="w-24 shrink-0 text-right text-xs text-muted-foreground">
                            {t('proposals.documents', { count: p.document_count })}
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    )
}
